import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@egaranti/components";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Search,
  X,
} from "lucide-react";

const SortableSearchableTableDialog = ({
  data,
  columns,
  title,
  searchPlaceholder,
  triggerButtonText,
  onSort,
  onSearch,
  renderCell,
  isOpen,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const triggerLabel =
    triggerButtonText ||
    t("extendedWarranty.reports.showAll", "Tümünü Gör");
  const searchPlaceholderText =
    searchPlaceholder ||
    t(
      "extendedWarranty.reports.searchPlaceholder",
      "Aramak için yazmaya başlayın...",
    );
  const emptySearchText = t(
    "extendedWarranty.reports.noSearchResults",
    "Arama kriterlerinize uygun sonuç bulunamadı.",
  );
  const emptyDataText = t(
    "extendedWarranty.reports.noData",
    "Veri bulunamadı.",
  );

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getFilteredData = () => {
    if (!data) return [];

    let filteredData = data;

    // Apply search filter
    if (searchQuery.trim() && onSearch) {
      const query = searchQuery.toLowerCase().trim();
      filteredData = data.filter((item) => onSearch(item, query));
    }

    return filteredData;
  };

  const getSortedData = () => {
    const filteredData = getFilteredData();
    if (!sortConfig.key || !onSort) return filteredData;

    const sortedData = [...filteredData].sort((a, b) =>
      onSort(a, b, sortConfig.key, sortConfig.direction),
    );

    return sortedData;
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown className="ml-1 h-3 w-3 text-gray-400" />;
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="ml-1 h-3 w-3 text-blue-600" />
    ) : (
      <ChevronDown className="ml-1 h-3 w-3 text-blue-600" />
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="secondaryColor" size="sm">
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-6xl bg-white">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <div className="relative mt-4">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={searchPlaceholderText}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-10 leading-5 placeholder-gray-500 focus:border-blue-500 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
            />
            {searchQuery && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <button
                  onClick={() => setSearchQuery("")}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </DialogHeader>
        <div className="max-h-[600px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead
                    key={column.key}
                    className={column.align === "right" ? "text-right" : ""}
                  >
                    {column.sortable ? (
                      <button
                        className={`flex items-center transition-colors hover:text-blue-600 ${
                          column.align === "right" ? "w-full justify-end" : ""
                        } ${
                          sortConfig.key === column.key ? "text-blue-600" : ""
                        }`}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                        {getSortIcon(column.key)}
                      </button>
                    ) : (
                      column.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {getSortedData().length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="py-8 text-center text-gray-500"
                  >
                    {searchQuery ? emptySearchText : emptyDataText}
                  </TableCell>
                </TableRow>
              ) : (
                getSortedData().map((item, index) => (
                  <TableRow key={index}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        className={column.align === "right" ? "text-right" : ""}
                      >
                        {renderCell(item, column.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SortableSearchableTableDialog;
